import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Joyride, { STATUS, ACTIONS, EVENTS } from 'react-joyride';
import { useTour } from '../contexts/TourContext';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../services/api';

// mobiel 2026-09-10 (audit patroon 14): onder 1024 px (dashboard-sidebar dicht, mobiele header) krijgt de
// tour een smallere tooltip, kleinere knoppen en een ander doel voor de berichten-stap.
const MOBILE_QUERY = '(max-width: 1023px)';
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(MOBILE_QUERY).matches
  );
  useEffect(() => {
    const mq = window.matchMedia(MOBILE_QUERY);
    const onChange = (e) => setIsMobile(e.matches);
    if (mq.addEventListener) mq.addEventListener('change', onChange);
    else mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', onChange);
      else mq.removeListener(onChange);
    };
  }, []);
  return isMobile;
};

// Creator tour steps (koppen/alinea's met sm:-variant: audit OnboardingTour.jsx:13-18, 84-98)
const buildCreatorSteps = (isMobile) => {
  // mobiel 2026-09-10 (audit OnboardingTour.jsx:68): onder 1024 px staat de sidebar (target
  // sidebar-messages) met -translate-x-full buiten beeld; Joyride zette spotlight en tooltip dan op x < 0.
  // Op mobiel richten we op de berichten-knop in de mobiele header (data-tour="mobile-messages",
  // DarkDashboardPage.jsx:551). Staat dat element (nog) niet in de DOM op het moment dat de tour start, dan
  // valt de stap terug op een gecentreerde uitleg; het desktop-doel is op mobiel immers precies het
  // buiten beeld staande sidebar-item waar deze fix om draait.
  const mobileTarget = '[data-tour="mobile-messages"]';
  const hasMobileTarget = isMobile && typeof document !== 'undefined' && !!document.querySelector(mobileTarget);
  const messagesContent = (
    <div>
      <h3 className="text-lg sm:text-xl font-bold mb-2">💬 Berichten</h3>
      <p>
        Communiceer direct met bedrijven over projecten. Snelle reacties verhogen je
        succeskansen!
      </p>
    </div>
  );
  const messagesStep = !isMobile
    ? { target: '[data-tour="sidebar-messages"]', content: messagesContent, placement: 'top' }
    : hasMobileTarget
      ? { target: mobileTarget, content: messagesContent, placement: 'bottom' }
      : {
          target: 'body',
          placement: 'center',
          content: (
            <div>
              <h3 className="text-lg sm:text-xl font-bold mb-2">💬 Berichten</h3>
              <p>
                Communiceer direct met bedrijven over projecten via het berichten-icoon rechtsboven.
                Snelle reacties verhogen je succeskansen!
              </p>
            </div>
          ),
        };

  return [
  {
    target: 'body',
    content: (
      <div>
        <h2 className="text-xl sm:text-2xl font-bold mb-3 sm:mb-4">Welkom bij UGC4you! 🎉</h2>
        <p className="text-base sm:text-lg mb-2">
          Als <strong>Creator</strong> kun je hier betaalde opdrachten vinden en geld verdienen met je content!
        </p>
        <p className="text-gray-600">
          Laten we je laten zien hoe alles werkt. Klik op "Volgende" om te beginnen.
        </p>
      </div>
    ),
    placement: 'center',
    disableBeacon: true,
  },
  {
    target: '[data-tour="dashboard-stats"]',
    content: (
      <div>
        <h3 className="text-lg sm:text-xl font-bold mb-2">📊 Jouw Statistieken</h3>
        <p>
          Hier zie je een overzicht van je verdiensten, actieve projecten en succesratio.
          Naarmate je meer opdrachten afrondt, groeien deze cijfers!
        </p>
      </div>
    ),
    placement: 'bottom',
  },
  {
    target: '[data-tour="action-projects"]',
    content: (
      <div>
        <h3 className="text-lg sm:text-xl font-bold mb-2">🎯 Projecten Vinden</h3>
        <p>
          Klik hier om alle beschikbare projecten te bekijken. Je kunt filteren op categorie,
          budget en deadline om projecten te vinden die bij jou passen.
        </p>
      </div>
    ),
    placement: 'top',
  },
  {
    target: '[data-tour="action-profile"]',
    content: (
      <div>
        <h3 className="text-lg sm:text-xl font-bold mb-2">👤 Jouw Profiel</h3>
        <p>
          Een sterk profiel is cruciaal! Voeg je portfolio, social media en vaardigheden toe.
          Bedrijven kiezen creators op basis van hun profiel.
        </p>
        <p className="mt-2 text-sm text-amber-600 font-semibold">
          💡 Tip: Voltooi minimaal 80% van je profiel voor meer kansen!
        </p>
      </div>
    ),
    placement: 'top',
  },
  messagesStep,
  {
    target: 'body',
    content: (
      <div>
        <h2 className="text-xl sm:text-2xl font-bold mb-3 sm:mb-4">🚀 Je bent er klaar voor!</h2>
        <p className="text-base sm:text-lg mb-3 sm:mb-4">
          Zo werkt het proces:
        </p>
        <ol className="list-decimal list-inside space-y-1.5 sm:space-y-2 mb-4 text-sm sm:text-base">
          <li>Browse projecten en vind opdrachten die bij je passen</li>
          <li>Dien een voorstel in met je prijs en aanpak</li>
          <li>Wacht op goedkeuring van het bedrijf</li>
          <li>Maak de content en lever het in</li>
          <li>Ontvang betaling via Stripe! 💸</li>
        </ol>
        <p className="text-gray-600">
          Succes met je eerste project! Je kunt deze tour altijd opnieuw starten via het help-icoon.
        </p>
      </div>
    ),
    placement: 'center',
  },
  ];
};

// Voor bedrijven vervangt deze uitlegvideo (Remotion, 63s, echte schermopnames)
// de klik-voor-klik rondleiding: één keer kijken legt de hele flow uit van
// opdracht plaatsen tot goedgekeurde video. Creators houden de Joyride-tour.
const CompanyWelcomeVideo = ({ onClose }) => {
  // mobiel 2026-09-10 (audit OnboardingTour.jsx:109): body-scroll-lock zolang de video-modal open is,
  // anders scrolt het dashboard mee bij vegen op de overlay.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  return (
  <div
    className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/60 p-4"
    onClick={onClose}
  >
    {/* mobiel 2026-09-10 (audit :112-113): max-h + overflow-y-auto zodat in landscape (640x360) sluitknop en "Aan de slag" bereikbaar blijven */}
    <div
      className="w-full max-w-2xl max-h-[calc(100dvh-2rem)] overflow-y-auto overscroll-contain rounded-2xl bg-white shadow-2xl"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="px-6 pt-6 pb-4 sm:px-8 sm:pt-8">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900">
              Zo werkt het: van opdracht tot goedgekeurde video
            </h2>
            <p className="mt-2 text-sm sm:text-base text-gray-600">
              Bekijk in één minuut hoe je een content creator inhuurt via UGC4you — met echte beelden van het platform.
            </p>
          </div>
          <button
            onClick={onClose}
            aria-label="Sluiten"
            className="shrink-0 rounded-full p-2.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>
      <div className="px-6 sm:px-8">
        <video
          className="w-full rounded-xl border border-gray-200"
          controls
          playsInline
          preload="none"
          poster="/uploads/videos/showcase/ugc4you-dashboard-uitleg-poster.webp"
        >
          <source src="/uploads/videos/showcase/ugc4you-dashboard-uitleg.mp4" type="video/mp4" />
        </video>
      </div>
      <div className="flex items-center justify-end gap-3 px-6 py-5 sm:px-8 sm:py-6">
        <button
          onClick={onClose}
          className="rounded-lg bg-[#0ea5e9] px-5 py-2.5 text-sm sm:text-base font-bold text-white hover:bg-[#0284c7] transition-colors"
        >
          Aan de slag
        </button>
      </div>
    </div>
  </div>
  );
};

const OnboardingTour = () => {
  const { tourActive, stepIndex, setStepIndex, completeTour } = useTour();
  const { user, updateUser } = useAuth();

  const isMobile = useIsMobile();
  // Stappen per viewport; tourActive in de deps zodat de DOM-check voor het mobiele berichten-doel
  // opnieuw draait op het moment dat de tour start (1 s na mount, dan staat de mobiele header in de DOM).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const steps = useMemo(() => buildCreatorSteps(isMobile), [isMobile, tourActive]);

  // Iedereen krijgt de rondleiding. Eerder werd hij verborgen voor bedrijven zonder
  // KvK-nummer, maar dat is precies elk nieuw account: bij registratie is KvK optioneel
  // en via Google wordt er niet eens naar gevraagd. De rondleiding draait alleen bij het
  // allereerste dashboardbezoek, dus die voorwaarde zette hem voor de helft van de
  // bedrijven permanent uit. Het aanvullen van de KvK wordt afgedwongen op het
  // opdrachtformulier zelf, op het moment dat het er werkelijk toe doet.
  const shouldShowTour = tourActive && user;

  const markOnboardingComplete = useCallback(async () => {
    completeTour();
    try {
      await api.post('/users/complete-onboarding');
      updateUser({ hasCompletedOnboarding: true });
    } catch (error) {
      console.error('Failed to mark onboarding as completed:', error);
    }
  }, [completeTour, updateUser]);

  const handleJoyrideCallback = useCallback(async (data) => {
    const { action, index, status, type } = data;

    if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
      // Tour completed or skipped
      await markOnboardingComplete();
    } else if (action === ACTIONS.CLOSE) {
      // User clicked the X (close) button - also mark as completed
      await markOnboardingComplete();
    } else if (type === EVENTS.STEP_AFTER || type === EVENTS.TARGET_NOT_FOUND) {
      // Update step index
      setStepIndex(index + (action === ACTIONS.PREV ? -1 : 1));
    }
  }, [markOnboardingComplete, setStepIndex]);

  if (!shouldShowTour) {
    return null;
  }

  // Bedrijven (en bureaus) krijgen de uitlegvideo in plaats van de Joyride-tour;
  // sluiten telt als onboarding afgerond, net als bij de tour. De help-knop
  // (resetTour) opent voor hen dus opnieuw de video.
  if (user?.role !== 'creator') {
    return <CompanyWelcomeVideo onClose={markOnboardingComplete} />;
  }

  return (
    <Joyride
      steps={steps}
      stepIndex={stepIndex}
      run={shouldShowTour}
      continuous
      showProgress
      showSkipButton
      callback={handleJoyrideCallback}
      scrollOffset={isMobile ? 80 : 20}
      styles={{
        options: {
          primaryColor: '#0ea5e9',
          zIndex: 10000,
          // mobiel 2026-09-10 (audit :224-237): tooltip nooit breder dan het scherm min 24 px
          width: isMobile ? 'calc(100vw - 24px)' : 380,
        },
        tooltip: {
          borderRadius: '12px',
          padding: isMobile ? '16px' : '24px',
        },
        tooltipContainer: {
          textAlign: 'left',
        },
        buttonNext: {
          backgroundColor: '#0ea5e9',
          borderRadius: '8px',
          padding: isMobile ? '8px 14px' : '10px 20px',
          fontSize: isMobile ? '14px' : '16px',
          fontWeight: 'bold',
        },
        buttonBack: {
          color: '#6b7280',
          marginRight: isMobile ? '6px' : '10px',
        },
        buttonSkip: {
          color: '#9ca3af',
        },
      }}
      locale={{
        back: 'Vorige',
        close: 'Sluiten',
        last: 'Afronden',
        next: 'Volgende',
        // Met showProgress gebruikt Joyride deze sleutel in plaats van `next`;
        // zonder eigen waarde valt hij terug op het Engelse "Next (Step 1 of 7)".
        // mobiel 2026-09-10 (audit :224-237): korter label zodat Overslaan + Vorige + Volgende in 312 px passen
        nextLabelWithProgress: isMobile ? 'Volgende ({step}/{steps})' : 'Volgende ({step} van {steps})',
        skip: 'Overslaan',
      }}
    />
  );
};

export default OnboardingTour;
